<template>
  <div>
    <page-title
      icon="fa fa-calendar-alt"
      main="Agenda"
      sub="Agendar Visita"
      :help="true"
      titleHelp="Ajuda - Agenda"
      content="Nesta página temos um calendário geral.
               Para criação de uma visita a um cliente pressione o botão no canto superior direito 'Nova Visita'.
               Para criação de um evento pressione o botão no canto superior direito 'Novo Evento'.
               Para alteração de uma Visita ou Evento basta ir com o mouse em ações e clicar em Editar."
    >
      <template v-slot:actions>
        <base-button
          @click="editSchedule()"
          type="neutral"
          class="ml-3"
          size="sm"
          v-if="$can('update', 'user')"
          >Nova Visita
        </base-button>
        <base-button
          @click="editEvent()"
          type="neutral"
          class="ml-3 pd-resposive"
          size="sm"
          v-if="$can('update', 'user')"
          >Novo Evento
        </base-button>
      </template>
    </page-title>

    <!-- Calendar -->
    <card>
      <!-- Fullcalendar -->
      <!-- Card header -->
      <div>
        <!-- Title -->
        <div class="row py-4">
          <div class="col-12 text-center">
            <h2 class="h2 mb-0">Calendário {{ actualYear }}</h2>
            <nav
              aria-label="breadcrumb"
              class="d-none d-md-inline-block ml-md-4"
            ></nav>
            <h3 class="h3 mb-0">
              DIA {{ actualDay }} de {{ actualMonth }}
            </h3>
            <h3 class="h3 mb-0"></h3>
          </div>
          <div class=" col-12 mt-3 text-center">
            <base-button
              class="btn-sm"
              type="neutral"
              size="sm"
              :class="{ active: defaultView === 'dayGridMonth' }"
              @click="changeView('dayGridMonth')"
              >Mes</base-button
            >
            <base-button
              class="btn btn-sm btn-default"
              type="neutral"
              size="sm"
              :class="{ active: defaultView === 'dayGridWeek' }"
              @click="changeView('dayGridWeek')"
              >Semana</base-button
            >
            <base-button
              class="btn btn-sm btn-default"
              type="neutral"
              size="sm"
              :class="{ active: defaultView === 'timeGridDay' }"
              @click="changeView('timeGridDay')"
              >Dia</base-button
            >
          </div>
          <div class="col-12 mt-3 text-center">
            <a
              href="#"
              @click.prevent="prev"
              class="fullcalendar-btn-prev btn-lg"
              type="neutral"
            >
              <i class="fas fa-angle-left" style="font-size: 30px;"></i>
            </a>
            <a
              href="#"
              @click.prevent="next"
              class="fullcalendar-btn-next btn-lg"
              type="neutral"
            >
              <i class="fas fa-angle-right"  style="font-size: 30px;"></i>
            </a>
          </div>
        </div>
      </div>
      <!-- Card body -->
      <div class="card-body p-0 card-calendar-body">
        <full-calendar
          :events="visitas"
          :plugins="calendarPlugins"
          :editable="true"
          contentHeight="auto"
          :theme="false"
          :selectable="true"
          :selectHelper="true"
          ref="fullCalendar"
          class="calendar"
          locale="brasil"
          :defaultView="defaultView"
          @dateClick="onDateClick"
          @eventClick="onEventClick"
        ></full-calendar>
      </div>
    </card>

    <!-- Edit schedule modal -->
    <modal :show.sync="modals.schedule">
      <div class="col-12" v-if="!scheduleForm.id">
        <div class="no-wrap">
          <base-button
            @click="editEvent(scheduleForm); modals.schedule = false"
            type="neutral"
            class="ml-3"
            size="sm"
            >Novo Evento
          </base-button>
        </div>
      </div>
      <div class="col-12" v-else>
        <div class="no-wrap">
          <base-button
            @click="deleteSchedule(scheduleForm.id);"
            type="danger"
            class="ml-3"
            size="sm"
            >Cancelar Visita
          </base-button>
        </div>
      </div>

      <form class="new-event--form" @submit="saveSchedule">
        <schedule-form v-model="scheduleForm" :disabledDate="disabledDate" ref="scheduleForm"/>
      </form>

      <template slot="footer">
        <button
          type="submit"
          class="btn btn-primary new-event--add"
          @click="saveSchedule"
        >
          Salvar
        </button>
        <button
          type="button"
          class="btn btn-link ml-auto"
          @click="closeAndClear('schedule')"
        >
          Cancelar
        </button>
      </template>
    </modal>

    <!-- Edit event modal -->
    <modal :show.sync="modals.event">
      <div class="col-12" v-if="!eventForm.id">
        <div class="no-wrap">
          <base-button
            @click="editSchedule(eventForm); modals.event = false"
            type="neutral"
            class="ml-3"
            size="sm"
            >Nova Visita
          </base-button>
        </div>
      </div>

      <form class="new-event--form" @submit="saveEvent">
        <event-form v-model="eventForm" :disabledDate="disabledDate" ref="eventForm"  />
      </form>

      <template slot="footer">
        <button
          type="submit"
          class="btn btn-primary new-event--add"
          @click="saveEvent"
        >
          Salvar
        </button>
        <button
          type="button"
          class="btn btn-link ml-auto"
          @click="closeAndClear('event')"
        >
          Cancelar
        </button>
      </template>
    </modal>
  </div>
</template>
<script>
import Modal from "@/components/Modal";
import moment from "moment";
import Multiselect from "@/components/Multiselect/translated_multiselect.vue";

import ScheduleForm from "@/components/Schedules/ScheduleForm.vue";
import EventForm from "@/components/Events/EventForm.vue";
import PageTitle from "@/layouts/PageTitle";
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { DatePicker, Cascader, Image } from "element-ui";
import { ValidationProvider, extend } from "vee-validate";
import { required } from "vee-validate/dist/rules";
import { apiUrl } from "@/backend/axios/index.js"

extend("required", {
  ...required,
  message: "Este campo é obrigatório",
});

const today = new Date();

export default {
  name: "calendar",
  components: {
    Modal,
    FullCalendar,
    Multiselect,
    PageTitle,
    [DatePicker.name]: DatePicker,
    [Cascader.name]: Cascader,
    [Image.name]: Image,
    ScheduleForm,
    EventForm,
  },
  data() {
    return {
      scheduleForm: {},
      eventForm: {
        color: "bg-purple",
      },
      active: true,
      event: {},
      dataAtual: today,
      calendarPlugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      defaultView: "dayGridMonth",
      date_start: "",
      date_end: "",
      schedules: [],
      events: [],
      modals: {
        event: false,
        schedule: false,
      },
    };
  },
  methods: {
    closeAndClear(modal) {
      if(modal == 'event'){
        this.$refs.eventForm.clearForm(); 
        this.modals.event = false;
      } else if (modal == 'schedule'){
        this.$refs.scheduleForm.clearForm(); 
        this.modals.schedule = false;
      }
    },
    setError(error, message) {
      console.warn("[AgndarVisita][setError]", message, error);
    },
    editSchedule(schedule = {}) {
      this.scheduleForm = schedule;
      this.modals.schedule = true;
    },
    editEvent(event = { color: "bg-purple" }) {
      this.eventForm = event;
      this.modals.event = true;
    },
    disabledDate(time) {
      return time.getTime() < Date.now() - 1000 * 60 * 60 * 24;
    },
    async getSchedules() {
      try {
        const response = await this.$http.secured.get(`/api/v1/users/${this.$currentUser().id}/schedules`, {
          params: { q: { status: this.active }, unpaged: true },
        })
        if (response.data) {
          response.data.forEach((visit) => visit.color = visit.color)
          this.schedules = response.data;
        }
      } catch (error) {
        console.warn("[AgendarVisita][getSchedules]", error)
      }
    },
    validateSchedule() {
      const re = /\S+@\S+\.\S+/;
      if (this.scheduleForm.email && !re.test(this.scheduleForm.email)) {
        return false;
      }

      return true;
    },
    deleteSchedule(id) {
      this.$http.secured.delete(`/api/v1/users/${this.$currentUser().id}/schedules/${id}`)
        .then(() => {
          this.schedules = this.schedules.filter(schedule => schedule.id !== id);
          this.modals.schedule = false
          this.$toasted.global.defaultSuccess({ msg: "Visita Cancelada",
          })    
        }) 
        .catch(error => {
          console.warn("[AgendarVisita][deleteSchedule]", error);
          this.$toasted.global.defaultError({ msg: "Não foi possível cancelar a visita.",
          });
        });
    },
    async saveSchedule() {
      if (!this.validateSchedule()) return;

      if (!this.scheduleForm.client.id) {
        this.$toasted.global.defaultError({ msg: "Cliente não pode ficar em branco" });
        return;
      }

      const formData = new FormData();

      for (const key in this.scheduleForm) {
        if (this.scheduleForm.hasOwnProperty(key)) {
          if (key === 'media') continue;

          const value = this.scheduleForm[key];
          formData.append(`schedule[${key}]`, value);
        }
      }

      if (this.scheduleForm.media && this.scheduleForm.media.length > 0) {
        for (const media of this.scheduleForm.media) {
          if (media.signed_id) {
            formData.append('schedule[media][]', media.signed_id);
          } else {
            formData.append('schedule[media][]', media);
          }
        }
      }
      try {
        if (this.scheduleForm.id) {
          await this.$http.secured.put(`/api/v1/users/${this.$currentUser().id}/schedules/${this.scheduleForm.id}`, formData)
        } else {
          await this.$http.secured.post(`/api/v1/users/${this.$currentUser().id}/schedules`, formData)
        }

        this.getSchedules();
        this.modals.schedule = false;
      } catch (error) {
        console.warn("[AgendarVisita][saveSchedule]", error)

        if (error.response && error.response.data && error.response.data.errors) {
          this.$toasted.global.defaultError({
            msg: error.response.data.errors[0],
          });
        } else {
          this.$toasted.global.defaultError({
            msg: "Não foi possivel criar a visita!",
          });
        }
      }
    },
    calendarApi() {
      return this.$refs.fullCalendar.getApi();
    },
    changeView(viewType) {
      this.defaultView = viewType;
      this.calendarApi().changeView(viewType);
    },
    next() {
      this.calendarApi().next();
      this.dataAtual = this.calendarApi().getDate();
    },
    prev() {
      this.calendarApi().prev();
      this.dataAtual = this.calendarApi().getDate();
    },
    onDateClick({ date }) {
      if (this.disabledDate(date)) {
        this.$toasted.global.defaultError({ msg: "Escolha uma data válida !" });
        return;
      }
      this.editSchedule({
        date_start: date,
        date_end: date,
      });
      this.date_start = date;
      this.date_end = date;
    },
    async onEventClick(clickEvent) {
      if (this.disabledDate(clickEvent.event.start)) return;
      if (!(clickEvent.event && clickEvent.event.extendedProps)) return;

      const data = clickEvent.event.extendedProps.objectData;

      if (Object.keys(data).length === 0) return;

      if (data.class_name == "Event") {
        this.editEvent(data)
      }
      if (data.class_name == "Schedule") {
        this.editSchedule(data)
      }
    },
    validateEmail(email) {
      const re = /^$|\S+@\S+\.\S+/;
      return re.test(email) ? "" : "Email inválido";
    },
    validateEvent() {
      if ((this.eventForm.free_array || []).find(email => this.validateEmail(email))) {
        return false;
      }

      return true;
    },
    async saveEvent() {
      if (!this.validateEvent()) return;

      const formData = new FormData();

      for (const key in this.eventForm) {
        if (this.eventForm.hasOwnProperty(key)) {
          if (key === 'media') continue;

          const value = this.eventForm[key];
          formData.append(`event[${key}]`, value);
        }
      }

      if (this.eventForm.media && this.eventForm.media.length > 0) {
        for (const media of this.eventForm.media) {
          if (media.signed_id) {
            formData.append('event[media][]', media.signed_id);
          } else {
            formData.append('event[media][]', media);
          }
        }
      }

      try {
        if (this.eventForm.id) {
          await this.$http.secured.put(`/api/v1/events/${this.eventForm.id}`, formData)
        } else {
          await this.$http.secured.post(`/api/v1/events`, formData)
        }

        this.getEvents();
        this.modals.event = false;
      } catch (error) {
        console.warn("[AgendarVisita][saveEvent]", error)
        if (error.response && error.response.data && error.response.data.errors) {
          this.$toasted.global.defaultError({
            msg: error.response.data.errors[0],
          });
        } else {
          this.$toasted.global.defaultError({
            msg: "Não foi possivel criar o evento!",
          });
        }
      }
    },
    async getEvents() {
      this.$http.secured
        .get(`/api/v1/events`)
        .then((response) => {
          this.events = response.data;
        })
        .catch((error) => console.error("[AgendarVisita][getEvents]", error));
    },
  },
  mounted() {
    this.getEvents();
    this.getSchedules();
  },
  computed: {
    actualMonth() {
      return this.dataAtual
        .toLocaleString("pt-BR", { month: "long" })
        .toUpperCase();
    },
    actualYear() {
      return this.dataAtual
        .toLocaleString("pt-BR", { year: "numeric" })
        .toUpperCase();
    },
    actualDay() {
      return this.dataAtual.toLocaleString("pt-BR", { day: "numeric" });
    },
    visits() {
      return this.schedules.map((schedule) => {
        return {
          id: schedule.id,
          title: schedule.client && schedule.client.razao_social,
          email: schedule.email,
          address: schedule.address,
          start: schedule.date_start,
          end: schedule.date_end,
          note: schedule.note,
          className: schedule && schedule.color ? schedule.color.toLowerCase(): '',
          backendClassName: schedule.class_name,
          allDay: false,
          media: schedule.media,
          objectData: schedule,
        };
      });
    },
    eventos() {
      return this.events.map((event) => {
        return {
          id: event.id,
          title: event.name,
          email: event.email,
          address: event.address,
          start: event.date_start,
          end: event.date_end,
          note: event.note,
          className: event && event.color ? event.color.toLowerCase() : '',
          backendClassName: event.class_name,
          free_array: event.free_array,
          media: event.media,
          objectData: event,
        };
      });
    },
    visitas() {
      return this.eventos.concat(this.visits);
    },
  },
};
</script>
<style lang="scss">
@import "~@fullcalendar/core/main.css";
@import "~@fullcalendar/daygrid/main.css";
@import "~@fullcalendar/timegrid/main.css";
@import "~@/assets/sass/core/vendors/fullcalendar";
</style>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
.no-wrap {
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-end;
}

img{
  width: 200px; /* Largura desejada */
  height: 200px;
  padding: 5px;
  object-fit: cover;
}
input[type=file]::file-selector-button {
    background-color: #2dce89;
    color: #fff;
    padding: 6px;
    cursor: pointer;
    border-radius: 5px;
    border: none;
  }
.remove-button {
  background-color: #ff0000;
  color: #ffffff;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: 14px;
  cursor: pointer;
  margin-left: 5px;
  text-align: center; /* Centraliza o texto horizontalmente */
  line-height: 2px; /* Centraliza o texto verticalmente */
}

@media (max-width: 768px) {
  .calendar .fc-day-number{
    font-size: 0.8rem;
    width: 50px;
  }
  .pd-resposive{
    margin-top: 6px
  }
}

</style>
